import { useEffect, useMemo, useState } from 'react';
import { useMutation, useQuery } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import {
  ActionIcon,
  Alert,
  Badge,
  Button,
  Group,
  Select,
  Stack,
  Table,
  Text,
  TextInput,
} from '@mantine/core';
import { IconAlertCircle, IconPlus, IconTrash } from '@tabler/icons-react';
import { AppModal } from '../../shared/components/AppModal';
import { ApiError } from '../../shared/api/problem';
import { useWarehouses } from '../warehouses/useWarehouses';
import { inviteUser, listGroups, type InvitationGrantDto } from './api';

/** The locked group whose members reach everything, so scoping one is meaningless. */
const TENANT_ADMIN = 'TenantAdmin';

/** The "all warehouses" option's value. A Select cannot hold null, and '' reads as unset. */
const EVERYWHERE = 'all';

function sameGrant(a: InvitationGrantDto, b: InvitationGrantDto): boolean {
  return a.groupCode === b.groupCode && a.warehouseId === b.warehouseId;
}

interface Props {
  opened: boolean;
  onClose: () => void;
  onInvited: (email: string) => void;
}

/**
 * Inviting somebody into the tenant.
 *
 * A tenant does not CREATE users, it invites them: the server emails a link and the invitee sets
 * their own password. That is why there is no "new user" form anywhere in the product — an admin
 * who typed somebody else's password would know it, and the account would not be theirs.
 *
 * THE GRANTS TRAVEL WITH THE INVITATION, AND EACH ONE SAYS WHERE
 * Otherwise the first thing that happens after somebody accepts is that they sign in, see a menu
 * with nothing on it, and ring the person who invited them. Choosing here means they arrive able
 * to work.
 *
 * The warehouse half is new, and it closes a real gap: an invitation used to carry bare group
 * codes and acceptance granted each one with NO warehouse, which means every warehouse. So
 * inviting a picker for one 3PL client handed them every client's site, and nothing on this screen
 * said so because there was no way to say anything else.
 *
 * Same shape as the user access dialog on purpose — a list of grants rather than a grid of
 * checkboxes — because it is the same decision, and an invitation cannot be edited once it is out.
 */
export function InviteUserModal({ opened, onClose, onInvited }: Props) {
  const { t } = useTranslation();

  const [email, setEmail] = useState('');
  const [grants, setGrants] = useState<InvitationGrantDto[]>([]);
  const [addGroup, setAddGroup] = useState<string | null>(null);
  const [addWarehouse, setAddWarehouse] = useState<string>(EVERYWHERE);
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [submitError, setSubmitError] = useState<string | null>(null);

  const groups = useQuery({
    queryKey: ['groups'] as const,
    queryFn: ({ signal }) => listGroups(signal),
    enabled: opened,
  });

  // The tenant's warehouses, not the ones this administrator can reach: inviting somebody to a
  // warehouse you do not work in is ordinary, and the invite endpoint is cross-warehouse for it.
  const warehouses = useWarehouses();

  useEffect(() => {
    if (!opened) return;
    setEmail('');
    setGrants([]);
    setAddGroup(null);
    setAddWarehouse(EVERYWHERE);
    setErrors({});
    setSubmitError(null);
  }, [opened]);

  const groupName = useMemo(() => {
    const map = new Map<string, string>();
    for (const g of groups.data ?? []) map.set(g.groupCode, g.displayName);
    return map;
  }, [groups.data]);

  const warehouseLabel = useMemo(() => {
    const map = new Map<string, string>();
    for (const w of warehouses.data ?? []) map.set(w.warehouseId, w.warehouseCode);
    return map;
  }, [warehouses.data]);

  const activeWarehouses = useMemo(
    () => (warehouses.data ?? []).filter((w) => w.isActive),
    [warehouses.data],
  );

  // A tenant administrator reaches everything, so offering a warehouse for that group would be
  // offering a choice the server ignores.
  const scopeless = addGroup === TENANT_ADMIN;

  function add() {
    const candidate = pendingGrant();
    if (!candidate) return;
    setGrants((current) => [...current, candidate]);
    setAddGroup(null);
    setAddWarehouse(EVERYWHERE);
  }

  /**
   * The add row as a grant, when it holds one, or null.
   *
   * WHY SUBMIT USES THIS AND NOT JUST `grants`
   * ------------------------------------------
   * Filling in the row and pressing the dialog's own button — Send invitation, Save changes —
   * used to discard it silently, because only ADDED rows were in `grants`. Mike hit this while
   * inviting a warehouse operator scoped to WHS2: the invitation went out with no grants at all,
   * the API substituted its fallback, and the account arrived as read-only everywhere. Two
   * screens' worth of care about per-warehouse access, undone by a button nobody knew was
   * mandatory.
   *
   * Nobody fills in a row meaning to throw it away. Add is now only needed to enter a SECOND
   * grant, and the common case — one group, one warehouse — needs it not at all.
   */
  function pendingGrant(): InvitationGrantDto | null {
    if (!addGroup) return null;
    const warehouseId = scopeless || addWarehouse === EVERYWHERE ? null : addWarehouse;
    const candidate: InvitationGrantDto = { groupCode: addGroup, warehouseId };
    // Already listed: the row on screen wins, and adding it twice would be a duplicate the
    // server would have to reject.
    return grants.some((g) => sameGrant(g, candidate)) ? null : candidate;
  }

  /** Everything the dialog would save: the listed grants plus whatever the add row holds. */
  function allGrants(): InvitationGrantDto[] {
    const pending = pendingGrant();
    return pending ? [...grants, pending] : grants;
  }


  function remove(target: InvitationGrantDto) {
    setGrants((current) => current.filter((g) => !sameGrant(g, target)));
  }

  function describeWhere(grant: InvitationGrantDto): string {
    if (grant.groupCode === TENANT_ADMIN) return t('users.access.everywhereAdmin');
    if (grant.warehouseId === null) return t('users.access.everywhere');
    return warehouseLabel.get(grant.warehouseId) ?? t('users.access.unknownWarehouse');
  }

  const invite = useMutation({
    mutationFn: () => inviteUser(email.trim(), allGrants()),
    onSuccess: () => {
      onInvited(email.trim());
      onClose();
    },
    onError: (err) => {
      // "That email already belongs to somebody" is the common one, and the server says which.
      setSubmitError(
        err instanceof ApiError
          ? (err.problem.detail ?? err.problem.title ?? t('users.errors.inviteFailed'))
          : t('users.errors.inviteFailed'),
      );
    },
  });

  function onSubmit(e: React.FormEvent) {
    e.preventDefault();
    setSubmitError(null);
    const next: Record<string, string> = {};
    // Deliberately loose: the server owns the real rule, and a client regex that disagrees with it
    // rejects addresses that would have worked.
    if (!email.trim() || !email.includes('@')) next.email = t('users.errors.emailRequired');
    setErrors(next);
    if (Object.keys(next).length > 0) return;
    invite.mutate();
  }

  return (
    <AppModal opened={opened} onClose={onClose} title={t('users.invite')} width="md">
      <form onSubmit={onSubmit} noValidate>
        <Stack gap="md">
          <Text size="sm" c="dimmed">
            {t('users.inviteHint')}
          </Text>

          <TextInput
            type="email"
            label={t('users.email')}
            placeholder={t('users.emailPlaceholder')}
            value={email}
            onChange={(e) => setEmail(e.currentTarget.value)}
            error={errors.email}
            maxLength={256}
            required
          />

          <Text size="sm" c="dimmed">
            {t('users.access.hint')}
          </Text>

          {grants.length > 0 && (
            <Table striped withTableBorder>
              <Table.Thead>
                <Table.Tr>
                  <Table.Th>{t('users.access.group')}</Table.Th>
                  <Table.Th>{t('users.access.where')}</Table.Th>
                  <Table.Th w={48} />
                </Table.Tr>
              </Table.Thead>
              <Table.Tbody>
                {grants.map((g) => (
                  <Table.Tr key={`${g.groupCode}:${g.warehouseId ?? EVERYWHERE}`}>
                    <Table.Td>{groupName.get(g.groupCode) ?? g.groupCode}</Table.Td>
                    <Table.Td>
                      {g.warehouseId === null ? (
                        <Badge variant="light" color="blue">
                          {describeWhere(g)}
                        </Badge>
                      ) : (
                        describeWhere(g)
                      )}
                    </Table.Td>
                    <Table.Td>
                      <ActionIcon
                        variant="subtle"
                        color="red"
                        onClick={() => remove(g)}
                        aria-label={t('users.access.remove', {
                          group: groupName.get(g.groupCode) ?? g.groupCode,
                          where: describeWhere(g),
                        })}
                      >
                        <IconTrash size={16} />
                      </ActionIcon>
                    </Table.Td>
                  </Table.Tr>
                ))}
              </Table.Tbody>
            </Table>
          )}

          <Group align="flex-end" gap="sm" wrap="wrap">
            <Select
              label={t('users.access.group')}
              placeholder={t('users.access.groupPlaceholder')}
              data={(groups.data ?? []).map((g) => ({ value: g.groupCode, label: g.displayName }))}
              value={addGroup}
              onChange={setAddGroup}
              searchable
              w={{ base: '100%', sm: 200 }}
            />
            <Select
              label={t('users.access.where')}
              description={scopeless ? t('users.access.adminIsEverywhere') : undefined}
              disabled={scopeless}
              data={[
                { value: EVERYWHERE, label: t('users.access.everywhere') },
                ...activeWarehouses.map((w) => ({
                  value: w.warehouseId,
                  label: `${w.warehouseCode} — ${w.displayName}`,
                })),
              ]}
              value={scopeless ? EVERYWHERE : addWarehouse}
              onChange={(v) => setAddWarehouse(v ?? EVERYWHERE)}
              allowDeselect={false}
              searchable
              w={{ base: '100%', sm: 220 }}
            />
            <Button
              variant="default"
              leftSection={<IconPlus size={16} />}
              onClick={add}
              disabled={!addGroup}
            >
              {t('users.access.add')}
            </Button>
          </Group>

          {/* Said before they send it, not after: somebody who arrives with nothing has to be
              chased down and fixed, and the invitation cannot be edited once it is out. */}
          {/* allGrants, not grants: the warning must describe what SAVING would do. Keyed off
              the committed list alone it would claim nothing was granted while a filled-in row
              sat directly above it, about to be sent. */}
          {allGrants().length === 0 && (
            <Alert color="yellow" variant="light" icon={<IconAlertCircle size={16} />}>
              {t('users.inviteNoGroupsWarning')}
            </Alert>
          )}

          {submitError && (
            <Alert color="red" variant="light" icon={<IconAlertCircle size={16} />}>
              {submitError}
            </Alert>
          )}

          <Group justify="flex-end" gap="sm">
            <Button variant="default" onClick={onClose}>
              {t('shell.cancel')}
            </Button>
            <Button type="submit" color="invetrak" loading={invite.isPending}>
              {t('users.sendInvite')}
            </Button>
          </Group>
        </Stack>
      </form>
    </AppModal>
  );
}
